// client/src/coms/settings/sections/VoiceVideoSettings.js
// Personal Settings → Voice & Video: microphone sensitivity (noise gate), push-to-talk (desktop app), camera and
// screen sharing. Device choice lives under Audio Devices.
import React, { useState } from 'react';
import { Row, Section, Switch } from '../../personalisation/settingsControls';
import { DEFAULT_GATE_DB } from '../useDeviceSettings';
import useMicMeter from '../useMicMeter';
import { DeviceSelect, LevelMeter } from './AudioDeviceSettings';
import { DEFAULT_PTT_SHORTCUT } from '../useHotkeySettings';

const dbToUi = (db) => Math.round(((Math.max(-80, Math.min(-10, db)) + 80) / 70) * 100);
const uiToDb = (ui) => -80 + (Math.max(0, Math.min(100, ui)) / 100) * 70;

export function KeyCap({ children, muted }) {
  return <kbd className={`vx-keycap${muted ? ' is-muted' : ''}`}>{children}</kbd>;
}

export default function VoiceVideoSettings({ dev, keys }) {
  const [testing, setTesting] = useState(false);
  const meter = useMicMeter(testing, dev.inputId, { announce: true });

  return (
    <Section id="vx-cat-voice" title="Voice & Video" description="How Voxemera listens to your microphone, and the camera and screen you share. Changes apply straight away, also during a call.">
      <Row
        label="Microphone sensitivity"
        hint="Sound quieter than the line is not sent (a noise gate). Move it left if quiet words get cut off, right if background noise comes through."
        settingId="noise-gate"
        scope="device"
        onReset={() => dev.setGateDb(DEFAULT_GATE_DB)}
        isDefault={Math.round(dev.gateDb) === DEFAULT_GATE_DB}
      >
        <div className="vx-gate">
          <LevelMeter db={testing ? meter.db : -100} gateDb={dev.gateDb} />
          <div className="vx-slider">
            <input
              type="range"
              min={0}
              max={100}
              value={dbToUi(dev.gateDb)}
              onChange={(e) => dev.setGateDb(uiToDb(Number(e.target.value)))}
              aria-label="Microphone sensitivity"
              aria-valuetext={`${Math.round(dev.gateDb)} dB`}
              data-testid="settings-noise-gate"
            />
            <output className="vx-slider-value">{`${Math.round(dev.gateDb)} dB`}</output>
          </div>
          <button type="button" className={`vx-btn small${testing ? ' is-on' : ''}`} aria-pressed={testing} onClick={() => setTesting((v) => !v)}>
            {testing ? 'Stop listening' : 'Show my level'}
          </button>
        </div>
        {meter.error ? (
          <div className="vx-acct-error" role="alert">
            {meter.error}
          </div>
        ) : null}
      </Row>

      <Row
        label="Push-to-talk"
        hint={keys.inTauri ? 'Your microphone is only sent while you hold the key. It works while Voxemera is minimised.' : 'Available in the Voxemera desktop app, which can listen for a key while it is in the background. In the browser your microphone is sent whenever you are unmuted.'}
        settingId="ptt"
        scope="device"
      >
        {keys.inTauri ? (
          <div className="vx-inline">
            <Switch label="Push-to-talk" checked={keys.pttEnabled} onChange={(v) => keys.setPtt(v)} testId="settings-ptt" />
            <KeyCap muted={!keys.pttEnabled}>{keys.pttShortcut || DEFAULT_PTT_SHORTCUT}</KeyCap>
            <button type="button" className="vx-btn small" disabled={!keys.pttEnabled} onClick={() => keys.startCapture('ptt')}>
              {keys.capturing === 'ptt' ? 'Press a key… (Esc cancels)' : 'Change key'}
            </button>
            {keys.pttShortcut !== DEFAULT_PTT_SHORTCUT ? (
              <button type="button" className="vx-link-btn" disabled={!keys.pttEnabled} onClick={keys.resetPttKey}>
                Reset to {DEFAULT_PTT_SHORTCUT}
              </button>
            ) : null}
          </div>
        ) : (
          <span className="vx-set-value is-muted">Desktop app only</span>
        )}
      </Row>

      <Row label="Camera" hint="Used when you turn your camera on in a voice channel." settingId="camera" scope="device" onReset={() => dev.setVideoInputId('')} isDefault={!dev.videoInputId} resetLabel="Use system default">
        <DeviceSelect label="Camera" value={dev.videoInputId} options={dev.videoOptions} onChange={dev.setVideoInputId} emptyText="No cameras found" testId="settings-camera" />
        {dev.needsCameraPermission ? (
          <button type="button" className="vx-link-btn" onClick={() => dev.requestPermission('video')}>
            Show camera names
          </button>
        ) : null}
      </Row>

      <Row
        label="Screen sharing"
        hint="Choose Performance (720p 30 fps), Balanced (1080p 30 fps) or Quality (1080p 60 fps) each time you share, in the share dialog. Small groups connect directly; larger ones go through the voice server."
        settingId="screen"
        scope="device"
      >
        <span className="vx-set-value is-muted">Chosen when you share</span>
      </Row>
    </Section>
  );
}
